import { Button, Icon, SearchBox, Table, addToast } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';

import { TranslationsKeys } from '../../../../../../locales/translations';
import {
  studyPlanTitulationListRequest,
  studyPlanTitulationUpdateRequest,
} from '../api';
import useStudyPlanTitulationColumns from '../hooks/useStudyPlanTitulationColumns';
import { StudyPlanTitulationRow } from '../types';

/**
 * Detalle de derecho a grado de un nivel académico: el valor global del nivel
 * y los overrides por plan de estudio. Guarda acá mismo — es el único lugar
 * donde se editan estos valores.
 */
export function TitulationByStudyPlan({
  academicLevelId,
  academicLevelName,
  onBack,
}: {
  academicLevelId: number;
  academicLevelName: string;
  onBack: () => void;
}) {
  const { t } = useTranslation(
    TranslationsKeys.CONFIGURATION_FIANANTIAL_OTHERS,
  );
  const { control, watch, setValue } = useForm<{ search: string }>({
    defaultValues: { search: '' },
  });
  const search = watch('search') ?? '';

  const [rows, setRows] = useState<StudyPlanTitulationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // "general para todos" = todos los planes usan el valor global del nivel.
  // Estado propio (no derivado) para poder alternar entre el global y la
  // tabla por plan aunque todavía no exista ningún override.
  const [generalForAll, setGeneralForAll] = useState(true);
  // Valor global del nivel académico (aca_lev_tit_pay).
  const [globalAmount, setGlobalAmount] = useState<number | null>(null);
  const [titulationPaymentId, setTitulationPaymentId] = useState<number | null>(
    null,
  );

  // paymentRequired es DERIVADO, no un toggle manual: en modo "general para
  // todos" lo determina un monto global > 0; en modo por plan, la existencia de
  // algún override > 0. (En el backend, resolveTitulationPayment resuelve el
  // override de plan de forma autónoma; el flag de nivel aplica al monto global.)
  const paymentRequired = generalForAll
    ? (globalAmount ?? 0) > 0
    : rows.some((row) => row.hasOverride && (row.paymentAmount ?? 0) > 0);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await studyPlanTitulationListRequest(
      academicLevelId,
    );
    if (!error && data) {
      setRows(data.studyPlans);
      setGeneralForAll(data.studyPlans.every((row) => !row.hasOverride));
      setGlobalAmount(data.paymentAmount);
      setTitulationPaymentId(data.titulationPaymentId);
    }
    setLoading(false);
  }, [academicLevelId]);

  useEffect(() => {
    load();
  }, [load]);

  const onToggle = useCallback((studyPlanId: number, hasOverride: boolean) => {
    setRows((prev) =>
      prev.map((row) =>
        row.studyPlanId === studyPlanId
          ? {
              ...row,
              hasOverride,
              paymentAmount: hasOverride ? row.paymentAmount ?? 0 : null,
            }
          : row,
      ),
    );
  }, []);

  const onChangeAmount = useCallback((studyPlanId: number, amount: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.studyPlanId === studyPlanId
          ? { ...row, paymentAmount: amount === '' ? null : Number(amount) }
          : row,
      ),
    );
  }, []);

  const onToggleGeneral = useCallback(() => {
    setGeneralForAll((prev) => {
      const next = !prev;
      if (next) {
        // Volver a "general para todos": limpiar overrides (todos heredan).
        setRows((rs) =>
          rs.map((row) => ({
            ...row,
            hasOverride: false,
            paymentAmount: null,
            disabled: true,
          })),
        );
      } else {
        setRows((rs) =>
          rs.map((row) => ({
            ...row,
            disabled: false,
          })),
        );
      }
      return next;
    });
  }, []);

  const onSave = useCallback(async () => {
    setSaving(true);
    const { error } = await studyPlanTitulationUpdateRequest(academicLevelId, {
      paymentRequired,
      paymentAmount: paymentRequired ? globalAmount ?? 0 : undefined,
      titulationPaymentId,
      studyPlans: rows.map((row) => ({
        studyPlanId: row.studyPlanId,
        hasOverride: row.hasOverride,
        paymentAmount: row.hasOverride ? row.paymentAmount ?? 0 : undefined,
      })),
    });
    setSaving(false);
    if (error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('academicLevelTitulationPayment.detail.onSaveError'),
      });
      return;
    }
    addToast({
      icon: 'success',
      color: 'success',
      text: t('academicLevelTitulationPayment.detail.onSaveSuccess'),
    });
    // Recarga para tomar el titulationPaymentId recién creado: sin eso, un
    // segundo guardado del mismo nivel insertaría otra fila en vez de editarla.
    load();
  }, [
    academicLevelId,
    paymentRequired,
    globalAmount,
    titulationPaymentId,
    rows,
    t,
    load,
  ]);

  const columns = useStudyPlanTitulationColumns({ onToggle, onChangeAmount });

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return rows;
    }
    return rows.filter((row) =>
      `${row.name} ${row.modalityName ?? ''}`.toLowerCase().includes(term),
    );
  }, [rows, search]);

  return (
    <div className="w-100 d-flex flex-column">
      <div
        className="w-100 d-flex bg-secondary p-3"
        style={{ borderRadius: '8px 0px 0px 8px' }}
      >
        <button
          type="button"
          className="btn btn-text btn-secondary d-flex align-items-center"
          style={{ gap: 5 }}
          onClick={onBack}
        >
          <Icon name="chevron_left" color="white" size={12} />
          <span className="text-white">
            {t('academicLevelTitulationPayment.detail.back')}
          </span>
        </button>
      </div>
      <div
        className="w-100 d-flex p-3"
        style={{ backgroundColor: '#F7F7F7', height: 84 }}
      >
        <div className="flex-fill d-flex align-items-center">
          <span className="fs-16 fw-700 text-primary text-uppercase">
            {academicLevelName}
          </span>
        </div>
        <div style={{ maxWidth: 350, width: '100%' }}>
          <SearchBox
            name="search"
            label=""
            placeholder={t(
              'academicLevelTitulationPayment.detail.searchPlaceholder',
            )}
            disabled={generalForAll}
            control={control}
            clear={() => setValue('search', '')}
          />
        </div>
      </div>
      <div
        className="w-100 d-flex flex-column p-3"
        style={{
          borderLeft: '1px solid #F7F7F7',
          borderRight: '1px solid #F7F7F7',
          borderBottom: '1px solid #F7F7F7',
        }}
      >
        <div className="d-flex w-100 align-items-center" style={{ gap: 10 }}>
          <div className="flex-fill">
            <label className="g-checkbox-input-container d-flex align-items-center mt-3 mb-4">
              <Input
                className="g-checkbox-input"
                type="checkbox"
                checked={generalForAll}
                onChange={onToggleGeneral}
              />
              <span className="checkmark"></span>
              <span className="ml-2">
                {t('academicLevelTitulationPayment.detail.generalForAll')}
              </span>
            </label>
          </div>
          {!!generalForAll && (
            <div
              className="d-flex w-100 align-items-center justify-content-end"
              style={{ gap: 10 }}
            >
              <span className="fs-14 fw-700 text-primary text-uppercase">
                {t('academicLevelTitulationPayment.detail.table.paymentAmount')}
              </span>
              <div>
                <Input
                  type="number"
                  min={0}
                  className="border-primary"
                  style={{
                    width: '160px',
                    height: '38px',
                    textAlign: 'center',
                  }}
                  placeholder={t(
                    'academicLevelTitulationPayment.detail.table.paymentAmount',
                  )}
                  value={globalAmount ?? ''}
                  onChange={(e) =>
                    setGlobalAmount(
                      e.target.value === '' ? null : Number(e.target.value),
                    )
                  }
                />
              </div>
            </div>
          )}
        </div>

        <div
          className="border border-secondary"
          style={{ borderRadius: 8, overflow: 'hidden' }}
        >
          <Table
            columns={columns}
            data={generalForAll ? rows : filteredRows}
            isLoadingResults={loading}
            color={'transparent'}
          />
        </div>

        <div className="d-flex justify-content-end mt-3">
          <Button
            text={t('academicLevelTitulationPayment.detail.saveChanges')}
            size="md"
            disabled={saving || loading}
            loading={saving}
            onClick={onSave}
          />
        </div>
      </div>
    </div>
  );
}

export default TitulationByStudyPlan;
